import React, { useMemo } from 'react'
import moment from 'moment'
import { hapticTick } from '../../../../../_utils/haptics'
import { CAMPAIGNS_VISIBLE } from '../../../../../config/featureFlags'
import { ChannelIcon } from '../../plans/components/PlanCard'
import { SampleDataPrompt } from '../../../../marketing/campaigns/sampleCampaigns'
import { campaignPath, NEW_PATH } from '../../campaigns/data/open'
import { campaignResults, funnel, inRange, rate } from '../data/results'
import { Card, Empty, FunnelRow, pctText, plural } from './parts'

const MANY = { post: 'posts', story: 'Stories', email: 'emails', sms: 'texts' }
const ONE = { post: 'post', story: 'Story', email: 'email', sms: 'text' }
const dates = c => (c.start ? (moment(c.start).isSame(c.end, 'day') ? moment(c.start).format('D MMM') : `${moment(c.start).format('D MMM')} to ${moment(c.end).format('D MMM')}`) : '')
// "Bailey’s ideas: 3 bookings from 2 campaigns"
const tally = (who, rows) => `${who}: ${plural(rows.reduce((n, r) => n + r.bookings, 0), 'booking', 'bookings')} from ${plural(rows.length, 'campaign', 'campaigns')}`

// Campaigns: each campaign's results, then emails and texts on their own
const CampaignsTab = ({ data, history, demo, onDemo }) => {
  const { sends, groups, posts } = data
  const inR = useMemo(() => (sends || []).filter(s => inRange(s.at, data.range)), [sends, data.range])
  const email = funnel(inR.filter(s => s.kind === 'email'))
  const sms = funnel(inR.filter(s => s.kind === 'sms'))
  const rows = useMemo(() => (CAMPAIGNS_VISIBLE ? campaignResults(groups, sends || [], posts, data.range) : []), [groups, sends, posts, data.range])
  const bailey = rows.filter(r => r.by === 'bailey')
  const yours = rows.filter(r => r.by === 'yours')
  const top = inR.slice().sort((a, b) => ((Number(b.stats.bookings) || 0) - (Number(a.stats.bookings) || 0)) || ((Number(b.stats.clicked) || 0) - (Number(a.stats.clicked) || 0)) || (b.at - a.at)).slice(0, 8)
  const go = (path, state) => { hapticTick(); history.push(path, state) }

  if (sends === undefined) return <div className="pf-tab"><span className="pf-skel is-chart" /></div>

  return (
    <div className="pf-tab">
      {!demo && !sends.length && <SampleDataPrompt onPreview={() => onDemo(true)} />}

      {CAMPAIGNS_VISIBLE && (
        <Card
          title="Campaigns"
          aside={rows.length ? <span className="pf-aside">{[bailey.length && tally('Bailey’s ideas', bailey), yours.length && tally('Yours', yours)].filter(Boolean).join(' · ')}</span> : null}
        >
          {rows.length ? (
            <div className="pf-scroll">
              <div className="pf-table">
                <div className="pf-tr pf-th pf-t-camp"><span>Campaign</span><span>In it</span><span className="r">Reached</span><span className="r">Tapped</span><span className="r">Bookings</span><span className="r">Posts shared</span></div>
                {rows.map(r => (
                  <button key={r.id} type="button" className="pf-tr pf-t-camp" onClick={() => go(campaignPath(r.id), { from: '/marketing-performance' })}>
                    <span className="pf-name"><b>{r.name}</b><span className={`pf-who${r.by === 'yours' ? ' is-yours' : ''}`}>{r.by === 'bailey' ? 'Bailey' : 'Yours'}</span><small>{dates(r)}</small></span>
                    <span className="pf-in">{['post', 'story', 'email', 'sms'].filter(ch => r.items[ch]).map(ch => <span key={ch}><ChannelIcon channel={ch} size={11} />{r.items[ch]} {r.items[ch] === 1 ? ONE[ch] : MANY[ch]}</span>)}</span>
                    <span className="r">{r.sends ? r.reached.toLocaleString('en-GB') : '·'}</span>
                    <span className="r">{r.sends ? r.tapped.toLocaleString('en-GB') : '·'}</span>
                    <span className={`r${r.bookings ? ' pf-pos' : ''}`}>{r.sends ? r.bookings.toLocaleString('en-GB') : '·'}</span>
                    <span className="r">{r.posts ? `${r.shared} of ${r.posts}` : '·'}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <Empty title="No campaigns ran in these dates" action={<button type="button" className="pf-btn" onClick={() => go(NEW_PATH, { from: '/marketing-performance' })}>New campaign</button>}>
              Posts, Stories, emails and texts together. Their results show here once they’ve gone out.
            </Empty>
          )}
          {rows.length > 0 && <p className="pf-foot">Campaigns are kept on the device that made them for now, so they show here on this one. The emails and texts in them are counted everywhere.</p>}
        </Card>
      )}

      <div className="pf-row2">
        <Card title="Emails" aside={<span className="pf-aside">{plural(email.count, 'sent', 'sent')}</span>}>
          {email.count ? (
            <div className="pf-funnel">
              <FunnelRow label="Delivered" value={email.delivered} base={email.delivered} first />
              <FunnelRow label="Opened" value={email.opened} base={email.delivered} quiet />
              <FunnelRow label="Tapped" value={email.clicked} base={email.delivered} />
              <FunnelRow label="Booked" value={email.bookings} base={email.delivered} />
            </div>
          ) : <p className="pf-muted">No emails went out in these dates.</p>}
          {email.count > 0 && <p className="pf-foot">Opens are shown small: phones open emails on their own, so taps and bookings say more.</p>}
        </Card>
        <Card title="Texts" aside={<span className="pf-aside">{plural(sms.count, 'sent', 'sent')}</span>}>
          {sms.count ? (
            <div className="pf-funnel">
              <FunnelRow label="Delivered" value={sms.delivered} base={sms.delivered} first />
              {sms.clicked > 0 && <FunnelRow label="Tapped" value={sms.clicked} base={sms.delivered} />}
              <FunnelRow label="Booked" value={sms.bookings} base={sms.delivered} />
            </div>
          ) : <p className="pf-muted">No texts went out in these dates.</p>}
        </Card>
      </div>

      <Card title="Emails and texts">
        {top.length ? (
          <div className="pf-scroll">
            <div className="pf-table">
              <div className="pf-tr pf-th pf-t-send"><span>Sent</span><span className="r">Delivered</span><span className="r">Tapped</span><span className="r">Bookings</span></div>
              {top.map(s => (
                <button key={s.id} type="button" className="pf-tr pf-t-send" disabled={demo} onClick={() => go('/marketing-campaigns', { openCampaignId: s.id })}>
                  <span className="pf-name"><b><ChannelIcon channel={s.kind} size={12} />{s.name}</b><small>{moment(s.at).format('ddd D MMM')}</small></span>
                  <span className="r">{(Number(s.stats.delivered) || 0).toLocaleString('en-GB')}</span>
                  <span className="r">{s.kind === 'sms' && !s.stats.clicked ? '·' : pctText(rate(Number(s.stats.clicked) || 0, Number(s.stats.delivered) || 0))}</span>
                  <span className={`r${s.stats.bookings ? ' pf-pos' : ''}`}>{s.stats.bookings || '·'}</span>
                </button>
              ))}
            </div>
          </div>
        ) : <p className="pf-muted">Nothing was sent in these dates.</p>}
        {(email.count > 0 || sms.count > 0) && (
          <div className="pf-health">
            {email.count > 0 && <span>Email delivery <b>{pctText(email.deliveryRate)}</b></span>}
            {sms.count > 0 && <span>Text delivery <b>{pctText(sms.deliveryRate)}</b></span>}
            <span className="pf-muted">Bounces and unsubscribes arrive with the upgraded sending engine.</span>
          </div>
        )}
      </Card>
    </div>
  )
}

export default CampaignsTab
